import { contactPrice } from '../contacts/perks';
import type { GameContext } from '../core/context';
import type { CrimeType, DamageSide } from '../core/events';
import { Vector3 } from 'three';
import { angleDelta, approach, type V2 } from '../core/math';
import type { Rng } from '../core/rng';
import type { System } from '../core/system';
import { Layer } from '../physics/types';
import type { PlayerService } from '../player/playerSystem';
import type { WeaponAnim } from '../render/characters/animation';
import type { Vehicle } from '../vehicles/types';
import type { PointOfInterest } from '../world/types';
import type { WpnHitResult, WpnState } from '../../shared/messages/weapons';
import { PlayerAction } from '../../shared/snapshot';
import { weaponFromWire, weaponWireId } from '../../shared/weapons';
import { Arsenal, type Haul } from './arsenal';
import { RUN_SPEED } from '../input/footShaping';
import { damageAt, FREE_AIM_CONE, FREE_AIM_KEEP_CONE, headingTo, keepChosenLock, meleeAssist, meleeTarget, pelletHeadings, pickLockTarget, resolveHit, stickAimHeading, traceShot, TARGET_RADIUS, type CombatTarget } from './combat';
import { capSpeed, LOCK_REACQUIRE_DELAY, lungeVelocity, seenWithGrace } from './lockFeel';
import { HP_RECOVER_AFTER, PERSON_HP, WEAPONS, fenceStock, isFirearm, isWeaponId, type WeaponId } from './weaponData';
import type { Pellet, WeaponPickup, WeaponService, WeaponStateInfo } from './types';

/**
 * Weapons (order 205, decision band, right after the player controller): the arsenal, melee swings,
 * aiming with a soft lock-on, firing (a physics raycast per round or pellet: walls and cars stop
 * bullets), reloading, pick-ups, the fence, and what weapons mean to the law: every blow and every
 * shot goes out as a `crime` event for heat to score, gunfire is heard (see heat), showing a gun to
 * a copper is brandishing, and the police escalate to armed units (src/police).
 *
 * Online (docs/WEAPONS.md §13) the arsenal is the SERVER's: weapons and rounds are inventory items,
 * mirrored here from `wpn` pushes into a separate Arsenal (the offline one and its save are never
 * touched). The hand, aim, swings and shots play locally as offline, but the targets are the other
 * players and the server's foot officers, and nothing is scored or hurt here: every blow and shot goes to the server as a `wpnHit`
 * claim (weapon, target, muzzle, heading, render time) and the server's verdict comes back.
 *
 * Talks to other modules only through services (player, peds, police, vehicles, economy, save, ui,
 * juice, cameraRig) and events. Presentation (meshes, fx, HUD) lives in weaponView.ts.
 */

/** Seconds a fired gun stays up before it is lowered again. */
const HOLD_UP_AFTER_SHOT = 0.9;
/** Recoil pose length (s). */
const RECOIL_TIME = 0.28;
/** A discharge is scored at most this often (a cylinder emptied in two seconds is one incident). */
const DISCHARGE_REPORT_GAP = 2.5;
/** Brandishing: checked this often while a gun is raised, against officers this close with a clear line. */
const BRANDISH_INTERVAL = 1.5;
const BRANDISH_RANGE = 32;
/** Unaimed rounds and running spoil the aim. */
const HIP_SPREAD = 1.8;
const MOVING_SPREAD = 1.3;
/** Lowered and idle this long with spare rounds: top the gun up. */
const TOP_UP_AFTER = 2.5;
/** Panic radius for a shot; people this close may cower instead of running. */
const GUNSHOT_PANIC = 45;
const GUNSHOT_COWER = 10;
const PICKUP_REACH = 1.1;
const FOUND_RESPAWN = 180;
const DROPPED_LIFETIME = 240;
const FENCE_REACH = 3.2;
/** A lock chosen by a tap lasts this long without the player aiming or firing at it. */
const CHOSEN_LOCK_IDLE = 8;
/**
 * A HIT / FIRE press that comes while the last swing or shot is still going is kept this long (s) and
 * goes off as soon as it can: taps a moment early are never lost (docs/CONTROLS-POLISH.md).
 */
export const ATTACK_BUFFER = 0.25;

export const FENCE_NAME = 'Sid Marlowe';

/** Where the street finds turn up (POI id, offset along its heading). The first that exists is used per weapon. */
const FOUND_SPOTS: readonly { weapon: WeaponId; poi: string; along: number; side: number }[] = [
  { weapon: 'bat', poi: 'mercy-square', along: 6, side: 3 },
  { weapon: 'crowbar', poi: 'gasworks-yard', along: 3, side: -2 },
  { weapon: 'crowbar', poi: 'lockup-arch-14', along: 3, side: 2.5 },
];

const keyOf = (t: { kind: string; id: number }): string => `${t.kind}:${t.id}`;

export function createWeaponSystem(): System {
  let ctx: GameContext;
  let rng: Rng;
  const arsenal = new Arsenal();
  let action: 'idle' | 'swing' | 'fire' | 'reload' = 'idle';
  let actionT = 0;
  let swingLanded = false;
  let swingFacing: number | null = null;
  /** The man a swing goes at (the lock or the assist): the wind-up lunges to close the gap to him. */
  let swingTarget: CombatTarget | null = null;
  /** Lock feel (lockFeel.ts): when the lock was last in sight, and until when the soft lock holds off after its man went down. */
  let lockSeenAt = -99;
  let lockHoldOffUntil = -1;
  /** Where the last hip shot went: the body turns that way while the gun is still up, lock or no lock. */
  let hipFacing: number | null = null;
  let raise = 0;
  let holdUpUntil = -99;
  let fireReadyAt = 0;
  let lastShotAt = -99;
  let lastAimAt = -99;
  let pendingReloadAt = -1;
  let dischargeReportAt = -99;
  let brandishAt = 0;
  let cowerAt = 0;
  let screamAt = -99;
  let dryToastAt = -99;
  let lock: CombatTarget | null = null;
  /** The target the player tapped (or right-clicked): it holds the lock without the cone. */
  let chosen: { kind: CombatTarget['kind']; id: number } | null = null;
  let chosenUntil = 0;
  let stanceSet = false;
  let wasActive = false;
  /** A buffered attack press goes off before this (sim time); -1 = none. */
  let attackUntil = -1;
  /** Free aim while aiming (the mouse cursor with the right button, a pad's right stick): the heading, or null. */
  let freeAim: number | null = null;
  const cursorRay = new Vector3();
  let shots = 0;
  let hits = 0;
  const health = new Map<string, { hp: number; at: number }>();
  const pickups: WeaponPickup[] = [];
  let nextPickupId = 1;
  let fence: { pos: V2; heading: number; name: string } | null = null;
  const anim: WeaponAnim = { hold: 'fist', move: 'none', t: 0, raise: 0 };
  const offs: (() => void)[] = [];
  // ---- online (the server's arsenal)
  const netArsenal = new Arsenal();
  /** Weapon we asked the server for and haven't heard back about (pushes don't undo the cycle meanwhile). */
  let pendingEquip: WeaponId | null = null;
  let aimSent = false;
  let nonceSeq = 0;
  const nonceSalt = Math.floor(Math.random() * 0x7fffffff).toString(36);
  let protectedToastAt = -99;

  const player = (): PlayerService | undefined => ctx.services.tryGet('player');
  const online = (): boolean => ctx.services.tryGet('economy')?.authority === 'server';
  /** The arsenal in play: the save's offline, the server's mirror online. */
  const ars = (): Arsenal => (online() ? netArsenal : arsenal);
  const def = () => WEAPONS[ars().current];
  const toast = (text: string, kind: 'info' | 'good' | 'bad' | 'cash' | 'warn' = 'info'): void => ctx.services.tryGet('ui')?.toast(text, kind);

  function inControl(): boolean {
    const p = player();
    const outcome = ctx.services.tryGet('police')?.outcome ?? 'none';
    return !!p && p.controlEnabled && ctx.state.mode === 'playing' && outcome === 'none';
  }

  function isActive(): boolean {
    return inControl() && player()!.mode === 'foot';
  }

  // ---------------------------------------------------------------- arsenal changes

  function persist(): void {
    if (online()) {
      // Online break-ins run on the server clock: a crowbar is just a crowbar there.
      player()?.setBreakInScale(1);
      return;
    }
    ctx.services.tryGet('save')?.setArsenal(arsenal.toSaved());
    player()?.setBreakInScale(def().breakInScale);
  }

  function changed(): void {
    cancelAction();
    attackUntil = -1;
    persist();
    ctx.events.emit('weaponChanged', { weapon: ars().current });
  }

  function cancelAction(): void {
    action = 'idle';
    actionT = 0;
    pendingReloadAt = -1;
    swingFacing = null;
    swingTarget = null;
  }

  function give(id: WeaponId, opts: { ammo?: number; select?: boolean } = {}): boolean {
    if (online()) return false; // online weapons are items: bought or looted, never handed out
    const isNew = arsenal.give(id);
    if (opts.ammo) arsenal.addAmmo(id, opts.ammo);
    if (opts.select ?? true) arsenal.select(id);
    changed();
    return isNew;
  }

  // ---------------------------------------------------------------- targets & damage

  function gatherTargets(center: V2, radius: number): CombatTarget[] {
    const out: CombatTarget[] = [];
    if (online()) {
      // Online the bodies that count are other players and the server's foot officers (entity ids); ambient
      // people are per-client scenery.
      const net = ctx.services.tryGet('net');
      for (const r of net?.remotePlayers() ?? []) {
        if (r.vehicle !== 0 || Math.hypot(r.x - center.x, r.z - center.z) > radius) continue;
        const down = r.action === PlayerAction.wasted || r.action === PlayerAction.knockdown;
        out.push({ kind: 'player', id: r.eid, x: r.x, z: r.z, r: TARGET_RADIUS, down });
      }
      for (const u of ctx.services.tryGet('police')?.units() ?? []) {
        if (u.kind !== 'foot' || Math.hypot(u.x - center.x, u.z - center.z) > radius) continue;
        out.push({ kind: 'cop', id: u.id, x: u.x, z: u.z, r: TARGET_RADIUS, down: u.role === 'disabled' });
      }
      return out;
    }
    const peds = ctx.services.tryGet('peds');
    peds?.forEachNear(center.x, center.z, radius, (x, z, id) => {
      const st = peds.stateOf(id);
      if (!st) return;
      out.push({ kind: 'ped', id, x, z, r: TARGET_RADIUS, down: st === 'down' || st === 'tumble' || st === 'getup' || peds.isDead(id) });
    });
    const police = ctx.services.tryGet('police');
    if (police) {
      for (const u of police.units()) {
        if (u.kind !== 'foot') continue;
        if (Math.hypot(u.x - center.x, u.z - center.z) > radius) continue;
        out.push({ kind: 'cop', id: u.id, x: u.x, z: u.z, r: TARGET_RADIUS, down: u.role === 'disabled' });
      }
    }
    return out;
  }

  /**
   * Cars as aim targets (never melee or bullet targets: they are physics bodies). Wrecks are skipped;
   * a car with nobody at the wheel standing still is `empty` (the automatic lock never picks it).
   */
  function vehicleTargets(center: V2, radius: number, out: CombatTarget[]): CombatTarget[] {
    const vehicles = ctx.services.tryGet('vehicles');
    if (!vehicles) return out;
    const mine = player()?.vehicle ?? null;
    for (const v of vehicles.query(center, radius)) {
      if (v.destroyed || v === mine) continue;
      const empty = v.occupant === null && v.controller !== 'ai' && v.speed < 1;
      out.push({ kind: 'vehicle', id: v.id, x: v.pos.x, z: v.pos.z, r: Math.max(v.spec.width, v.spec.length) / 2, down: false, empty });
    }
    return out;
  }

  /** Everything the aim can lock onto around `center`: people first in the list, then cars. */
  function lockTargets(center: V2, radius: number): CombatTarget[] {
    return vehicleTargets(center, radius, gatherTargets(center, radius));
  }

  const los = (a: V2, b: V2): boolean => ctx.physics.lineOfSight(a, b);

  /** Reach of the lock with the weapon in hand (m). */
  function lockRange(): number {
    const w = def();
    return w.firearm ? w.firearm.range : w.melee.reach + 4;
  }

  /**
   * Tap to target: lock onto this person or car (null clears a chosen lock). Only armed, on foot and in
   * control, and only someone within reach and in sight. Aiming online still goes through the server:
   * the lock only turns the shot; what it hits is the server's call (`claim`).
   */
  function chooseLock(t: { kind: CombatTarget['kind']; id: number } | null): boolean {
    if (!t) {
      if (chosen) lock = null;
      chosen = null;
      return true;
    }
    const p = player();
    if (!p || !isActive() || def().cls === 'unarmed') return false;
    const range = lockRange();
    const cur = lockTargets(p.pos, range + 2).find((c) => c.kind === t.kind && c.id === t.id) ?? null;
    if (!keepChosenLock(p.pos, cur, range, los)) return false;
    chosen = { kind: t.kind, id: t.id };
    chosenUntil = ctx.state.time + CHOSEN_LOCK_IDLE;
    lock = cur;
    return true;
  }

  function hpOf(t: { kind: string; id: number }): number {
    const h = health.get(keyOf(t));
    return h ? h.hp : PERSON_HP;
  }

  function crime(type: CrimeType, severity: number, pos: V2): void {
    ctx.events.emit('crime', { type, pos: { x: pos.x, z: pos.z }, severity, byPlayer: true });
  }

  /** A blow or a bullet lands on a person. `gun` decides which crime it is. */
  function hurt(t: CombatTarget, damage: number, lethal: boolean, push: V2, weapon: WeaponId, gun: boolean): void {
    if (t.kind === 'player') return; // the server decides what happens to players (claim())
    const now = ctx.state.time;
    const r = resolveHit(hpOf(t), damage, lethal);
    health.set(keyOf(t), { hp: r.hp, at: now });
    const pos = { x: t.x, z: t.z };
    if (t.kind === 'ped') {
      const peds = ctx.services.tryGet('peds');
      if (r.knocked) peds?.knockDown(t.id, push, null, true, { lethal: r.killed, crime: null, finish: true });
      else peds?.panic(pos, 0.8); // stung, and off they run
      if (gun) crime('shooting', 5, pos);
      if (r.killed) crime('pedKilled', 4, pos);
      else if (!gun) crime('assault', 3, pos);
    } else {
      const police = ctx.services.tryGet('police');
      // Knocking an officer down reports hitPolice itself (police module).
      if (r.knocked) police?.strikeOfficer(t.id, push, r.killed);
      else crime('hitPolice', 3, pos);
      if (gun) crime('shooting', 5, pos);
    }
    hits++;
    ctx.events.emit('weaponHit', { weapon, pos, target: t.kind, id: t.id, damage: Math.round(damage), knocked: r.knocked, killed: r.killed });
    if (gun) {
      ctx.services.tryGet('juice')?.hitStop(0.035);
      ctx.services.tryGet('cameraRig')?.shake(0.06);
    } // a blow's beat, kick and shake by its weight: fx/meleeFx.ts
  }

  function sideOf(v: Vehicle, point: V2): { side: DamageSide; local: V2 } {
    const l = v.body.worldToLocal(point);
    const along = Math.abs(l.z) / Math.max(0.1, v.spec.length / 2);
    const across = Math.abs(l.x) / Math.max(0.1, v.spec.width / 2);
    // Local +X is the vehicle's LEFT side (see docs/ARCHITECTURE.md).
    const side: DamageSide = along > across ? (l.z > 0 ? 'front' : 'rear') : l.x > 0 ? 'left' : 'right';
    return { side, local: { x: l.x, z: l.z } };
  }

  // ---------------------------------------------------------------- online claims

  const nonce = (): string => `wpn${nonceSalt}x${(++nonceSeq).toString(36)}`;

  /** Mirror the server's arsenal (a push or the answer to a request). */
  function applyServer(st: WpnState): void {
    const before = netArsenal.current;
    const cur = pendingEquip ?? weaponFromWire(st.w);
    netArsenal.syncFrom(st.owned.map(weaponFromWire), cur, st.rounds);
    if (netArsenal.current !== before) {
      cancelAction();
      ctx.events.emit('weaponChanged', { weapon: netArsenal.current });
    }
  }

  /** Ask the server for its view again (after a refused claim): equip what we think we hold, else fists. */
  function resync(): void {
    const net = ctx.services.tryGet('net');
    if (!net?.online) return;
    void net.request('wpnEquip', { w: weaponWireId(netArsenal.current) }).then((r) => {
      if (r.ok) applyServer(r.data as WpnState);
      else void net.request('wpnEquip', { w: 0 }).then((r2) => r2.ok && applyServer(r2.data as WpnState));
    });
  }

  function equipOnline(id: WeaponId): void {
    const net = ctx.services.tryGet('net');
    if (!net?.online) return;
    pendingEquip = id;
    void net.request('wpnEquip', { w: weaponWireId(id) }).then((r) => {
      if (pendingEquip === id) pendingEquip = null;
      if (r.ok) applyServer(r.data as WpnState);
      else resync();
    });
  }

  function sendAim(on: boolean): void {
    if (aimSent === on) return;
    aimSent = on;
    const net = ctx.services.tryGet('net');
    if (net?.online) void net.request('wpnAim', { on });
  }

  /**
   * A blow or a shot online: the claim goes to the server (weapon, who we think we hit, muzzle,
   * heading, the render time we saw them at); its verdict comes back with the arsenal.
   */
  function claim(weapon: WeaponId, target: CombatTarget | null, from: V2, heading: number): void {
    const net = ctx.services.tryGet('net');
    if (!net?.online) return;
    // Another player, or one of the server's officers (both are entity ids): the server judges either.
    const t = target?.kind === 'player' || target?.kind === 'cop' ? target : null;
    const msg = { w: weaponWireId(weapon), t: t?.id ?? 0, x: from.x, z: from.z, h: heading, rt: net.renderTime(1), n: nonce() };
    void net.request('wpnHit', msg).then((r) => {
      if (!r.ok) {
        if (r.error?.code !== 'rate_limited') resync();
        return;
      }
      const d = r.data as WpnHitResult;
      applyServer(d.state);
      if (!t) return;
      const gun = WEAPONS[weapon].firearm !== null;
      if (d.dmg > 0) {
        hits++;
        ctx.events.emit('weaponHit', { weapon, pos: { x: t.x, z: t.z }, target: t.kind, id: t.id, damage: d.dmg, knocked: false, killed: !!d.killed });
        if (gun) {
          ctx.services.tryGet('juice')?.hitStop(0.035);
          ctx.services.tryGet('cameraRig')?.shake(0.06);
        } // a blow: fx/meleeFx.ts
      } else if (d.blocked && ctx.state.time - protectedToastAt > 4) {
        protectedToastAt = ctx.state.time;
        toast('Protected: spawn cover or a safe zone. No harm done.', 'warn');
      }
    });
  }

  // ---------------------------------------------------------------- free aim

  /**
   * Where the player points, besides the way they face: a pad's right stick (screen-relative), or the
   * mouse cursor while the right button holds the aim (the ground point under it, at chest height).
   * Null = no free aim (touch, keys, a centred stick): the lock and the facing decide as before.
   */
  function freeAimHeading(from: V2): number | null {
    const input = ctx.input;
    const rig = ctx.services.tryGet('cameraRig');
    const s = input.aimStick();
    if (rig) {
      const h = stickAimHeading(s.x, s.y, rig.screenUp(), rig.screenRight());
      if (h !== null) return h;
    }
    const pt = input.aimCursor();
    if (!pt || !ctx.render) return null;
    const r = ctx.render.canvas.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    const cam = ctx.camera;
    cursorRay.set(((pt.x - r.left) / r.width) * 2 - 1, -((pt.y - r.top) / r.height) * 2 + 1, 0.5).unproject(cam).sub(cam.position);
    if (cursorRay.y >= -1e-4) return null;
    const t = (1.1 - cam.position.y) / cursorRay.y;
    const gx = cam.position.x + cursorRay.x * t;
    const gz = cam.position.z + cursorRay.z * t;
    if (Math.hypot(gx - from.x, gz - from.z) < 0.4) return null;
    return Math.atan2(gx - from.x, gz - from.z);
  }

  // ---------------------------------------------------------------- melee

  /**
   * The wind-up closes the gap to the man the swing goes at (lockFeel.ts `lungeVelocity`): a blow started
   * a step too far still lands instead of whiffing a foot short. Added on top of the walk the player
   * controller set this step, never above the sprint (the server's FOOT_MAX_SPEED), and not against a
   * stick pushed away from him (backing off while swinging is the player's call).
   */
  function lunge(p: PlayerService, reach: number, timeLeft: number): void {
    const t = swingTarget;
    if (!t) return;
    const cur = gatherTargets(p.pos, reach + 3).find((c) => c.kind === t.kind && c.id === t.id);
    if (!cur || cur.down) return;
    const v = lungeVelocity(p.pos, cur, cur.r, reach, timeLeft);
    if (v.x === 0 && v.z === 0) return;
    const vel = p.body.vel;
    const l = Math.hypot(v.x, v.z);
    if ((vel.x * v.x + vel.z * v.z) / l < -0.5) return;
    const next = capSpeed(vel.x + v.x, vel.z + v.z, RUN_SPEED);
    p.body.setVelocity(next, 0);
  }

  /** Heading of the move stick / keys on the ground (screen-relative), or null when it is not pushed much. */
  function moveHeading(): number | null {
    const input = ctx.input;
    const rig = ctx.services.tryGet('cameraRig');
    const up = rig ? rig.screenUp() : { x: 0, z: -1 };
    const right = rig ? rig.screenRight() : { x: 1, z: 0 };
    return stickAimHeading(input.axis('moveX'), input.axis('moveY'), up, right, 0.5);
  }

  function startSwing(): void {
    const p = player()!;
    const spec = def().melee;
    const assist = meleeAssist(p.pos, p.heading, spec, gatherTargets(p.pos, spec.reach + 2));
    swingTarget = lock && lock.kind !== 'vehicle' ? lock : assist;
    swingFacing = swingTarget ? headingTo(p.pos, swingTarget) : null;
    action = 'swing';
    actionT = 0;
    swingLanded = false;
    ctx.events.emit('weaponSwing', { weapon: ars().current, pos: { x: p.pos.x, z: p.pos.z }, heading: swingFacing ?? p.heading });
  }

  /**
   * The frame the swing connects: the one point everything a blow does hangs off. Today the timer in
   * update() fires it (`melee.hitAt` into the swing); when the swings get animation clips, the clip's hit
   * frame calls this instead and the timer goes. Emits `meleeContact` (hit or miss: the slash streak, the
   * swish's end), then `landSwing` decides who it hit (`weaponHit`).
   */
  function contact(): void {
    if (swingLanded) return;
    swingLanded = true;
    const p = player()!;
    const w = def();
    ctx.events.emit('meleeContact', { weapon: w.id, pos: { x: p.pos.x, z: p.pos.z }, heading: swingFacing ?? p.heading, reach: w.melee.reach });
    landSwing();
  }

  function landSwing(): void {
    const p = player()!;
    const w = def();
    const spec = w.melee;
    const heading = swingFacing ?? p.heading;
    const target = meleeTarget(p.pos, heading, spec, gatherTargets(p.pos, spec.reach + 1.5));
    if (online()) {
      claim(w.id, target, p.pos, heading);
      return;
    }
    const fx = Math.sin(heading);
    const fz = Math.cos(heading);
    if (target) {
      hurt(target, spec.damage, spec.lethal, { x: fx * spec.knockback, z: fz * spec.knockback }, w.id, false);
      return;
    }
    if (spec.vehicleDamage <= 0) return;
    // Nobody there: a bat or a crowbar takes it out on the nearest car in front instead.
    const at = { x: p.pos.x + fx * (spec.reach * 0.7), z: p.pos.z + fz * (spec.reach * 0.7) };
    const vehicles = ctx.services.tryGet('vehicles');
    const v = vehicles?.nearest(at, 2.2, (c) => !c.destroyed && c.occupant?.kind !== 'player');
    if (!v || !vehicles) return;
    const { side, local } = sideOf(v, at);
    vehicles.applyDamage(v, spec.vehicleDamage, side, true, local);
    ctx.events.emit('weaponHit', { weapon: w.id, pos: at, target: 'vehicle', id: v.id, damage: spec.vehicleDamage, knocked: false, killed: false });
    if (!v.ownedByPlayer && !v.stolen) crime('vehicleDamage', 1, at);
  }

  // ---------------------------------------------------------------- firearms

  function startReload(): void {
    action = 'reload';
    actionT = 0;
    pendingReloadAt = -1;
    ctx.events.emit('weaponReloadStarted', { weapon: ars().current });
    if (online()) {
      const net = ctx.services.tryGet('net');
      if (net?.online) void net.request('wpnReload', {}).then((r) => (r.ok ? applyServer(r.data as WpnState) : resync()));
    }
  }

  function fire(): void {
    const p = player()!;
    const w = def();
    const fa = w.firearm!;
    const now = ctx.state.time;
    if (!ars().useRound()) return;
    const pos = p.pos;
    const heading = lock ? headingTo(pos, lock) : (freeAim ?? p.heading);
    const from = { x: pos.x + Math.sin(heading) * 0.45, z: pos.z + Math.cos(heading) * 0.45 };
    const speed = Math.hypot(p.velocity.x, p.velocity.z);
    const spreadScale = (raise < 0.8 ? HIP_SPREAD : 1) * (speed > 3 ? MOVING_SPREAD : 1);
    const headings = pelletHeadings(heading, fa, () => rng.next(), spreadScale);
    const targets = gatherTargets(pos, fa.range + 2);
    const vehicles = ctx.services.tryGet('vehicles');
    const onTarget = new Map<string, { t: CombatTarget; dmg: number }>();
    const onVehicle = new Map<number, { v: Vehicle; dmg: number; point: V2 }>();
    const pellets: Pellet[] = [];
    for (const h of headings) {
      const tr = traceShot(ctx.physics, from, h, fa.range, targets, Layer.VEHICLE);
      pellets.push({ to: tr.point, hit: tr.hit, normal: tr.normal, label: tr.label, bodyId: tr.bodyId });
      const dmg = damageAt(fa, tr.distance);
      if (tr.hit === 'target' && tr.target) {
        const k = keyOf(tr.target);
        const cur = onTarget.get(k);
        if (cur) cur.dmg += dmg;
        else onTarget.set(k, { t: tr.target, dmg });
      } else if (tr.hit === 'vehicle' && tr.bodyId !== null && vehicles) {
        const v = vehicles.byBodyId(tr.bodyId);
        if (v) {
          const cur = onVehicle.get(v.id);
          if (cur) cur.dmg += dmg;
          else onVehicle.set(v.id, { v, dmg, point: tr.point });
        }
      }
    }
    shots++;
    lastShotAt = now;
    holdUpUntil = now + HOLD_UP_AFTER_SHOT;
    hipFacing = heading;
    fireReadyAt = now + fa.fireInterval;
    action = 'idle';
    ctx.events.emit('weaponFired', { weapon: w.id, from, heading, left: ars().loaded(w.id), pellets });
    ctx.services.tryGet('cameraRig')?.shake(fa.recoil);
    if (online()) {
      // The server judges it: claim the player most of the shot went into (or nobody). Cover is the
      // server's call (its colliders are the buildings and walls, and the networked cars; ambient traffic
      // and street furniture here are this client's scenery), so the claim is traced against walls alone.
      const count = new Map<CombatTarget, number>();
      for (const h of headings) {
        const tr = traceShot(ctx.physics, from, h, fa.range, targets, 0, { sightOnly: true });
        if (tr.hit === 'target' && tr.target) count.set(tr.target, (count.get(tr.target) ?? 0) + 1);
      }
      let best: CombatTarget | null = null;
      for (const [t, n] of count) if (!best || n > count.get(best)!) best = t;
      claim(w.id, best, from, heading);
      ctx.events.emit('pedPanic', { pos: { x: pos.x, z: pos.z }, radius: GUNSHOT_PANIC, reason: 'chaos' });
      ctx.services.tryGet('peds')?.cower(pos, GUNSHOT_COWER, 3.5, 0.55);
      if (ars().loaded(w.id) === 0 && ars().canReload()) pendingReloadAt = now + 0.35;
      return;
    }
    // Gunfire: always a crime (heard even when unseen), at most one report per burst.
    if (now - dischargeReportAt >= DISCHARGE_REPORT_GAP) {
      dischargeReportAt = now;
      crime('firearmDischarge', 4, pos);
    }
    const push = fa.pellets > 1 ? 5.5 : 3.5;
    for (const { t, dmg } of onTarget.values()) {
      const dx = t.x - pos.x;
      const dz = t.z - pos.z;
      const l = Math.hypot(dx, dz) || 1;
      hurt(t, dmg, true, { x: (dx / l) * push, z: (dz / l) * push }, w.id, true);
    }
    for (const { v, dmg, point } of onVehicle.values()) {
      if (!vehicles || v.destroyed) continue;
      const { side, local } = sideOf(v, point);
      vehicles.applyDamage(v, dmg * 0.3, side, true, local);
      ctx.events.emit('weaponHit', { weapon: w.id, pos: point, target: 'vehicle', id: v.id, damage: Math.round(dmg * 0.3), knocked: false, killed: false });
    }
    // The street reacts: everyone runs; the ones close by drop and cover their heads.
    ctx.events.emit('pedPanic', { pos: { x: pos.x, z: pos.z }, radius: GUNSHOT_PANIC, reason: 'chaos' });
    const peds = ctx.services.tryGet('peds');
    if (peds) {
      const cowering = peds.cower(pos, GUNSHOT_COWER, 3.5, 0.55);
      if ((cowering > 0 || peds.active > 0) && now - screamAt > 1.2) {
        screamAt = now;
        ctx.events.emit('pedScream', { pos: { x: pos.x, z: pos.z } });
      }
    }
    if (arsenal.loaded(w.id) === 0 && arsenal.canReload()) pendingReloadAt = now + 0.35;
  }

  /** HIT / FIRE. False when it can't go off yet (mid-swing, between shots): the caller keeps the press. */
  function onAttack(repeat = false): boolean {
    const w = def();
    const now = ctx.state.time;
    const fa = w.firearm;
    if (!fa) {
      if (action === 'swing') return false;
      startSwing();
      return true;
    }
    if (action === 'reload' || action === 'swing' || now < fireReadyAt) return false;
    // Held down, a gun keeps firing as it comes ready; it never dry-clicks or reloads on its own account.
    if (repeat && ars().loaded(w.id) === 0) return false;
    if (ars().loaded(w.id) > 0) {
      if (raise < 0.8 && !lock && now >= lockHoldOffUntil) {
        // Hip fire: grab whoever is in front (not in the beat after the last man went down).
        const p = player()!;
        lock = pickLockTarget(p.pos, p.heading, lockTargets(p.pos, fa.range + 2), fa.range, los);
        if (lock) lockSeenAt = now;
      }
      if (chosen) chosenUntil = now + CHOSEN_LOCK_IDLE;
      fire();
      return true;
    }
    if (ars().canReload()) {
      startReload();
      return true;
    }
    // Empty and nothing to load: pistol-whip whoever's in your face, otherwise just the click.
    const p = player()!;
    if (meleeTarget(p.pos, p.heading, w.melee, gatherTargets(p.pos, w.melee.reach + 1.5))) {
      startSwing();
      return true;
    }
    fireReadyAt = now + 0.3;
    ctx.events.emit('weaponDryFire', { weapon: w.id });
    if (now - dryToastAt > 8) {
      dryToastAt = now;
      toast(online() ? `Out of ${fa.ammo.name}. ${FENCE_NAME}'s tea chest at Gorringe's has more.` : `Out of ${fa.ammo.name}. ${FENCE_NAME} sells more.`, 'warn');
    }
    return true;
  }

  // ---------------------------------------------------------------- per step

  function updateHand(dt: number): void {
    const p = player()!;
    const now = ctx.state.time;
    const w = def();
    const fa = w.firearm;
    const input = ctx.input;
    const aimHeld = input.held('aim');
    if (aimHeld) lastAimAt = now;
    freeAim = aimHeld ? freeAimHeading(p.pos) : null;
    if (online()) sendAim(aimHeld);
    const wantUp = (aimHeld || now < holdUpUntil) && action !== 'reload';
    const rate = 1 / (fa ? fa.raiseTime : 0.12);
    raise = approach(raise, wantUp ? 1 : 0, (wantUp ? rate : rate * 1.5) * dt);

    // A tapped target holds the lock (no cone) while it stands, in reach and in sight; aiming keeps it fresh.
    const range = lockRange();
    const prevLock = lock;
    let held = false;
    // The lock we hold counts as in sight for a beat after it was last seen (lockFeel.ts: no blinking at a
    // lamp post or a wall corner); anybody new still needs a clear line.
    let kept: CombatTarget | null = null;
    const losKept = (a: V2, b: V2): boolean => (b === kept && seenWithGrace(false, lockSeenAt, now)) || los(a, b);
    if (chosen) {
      const c = chosen;
      const cur = w.cls === 'unarmed' || now > chosenUntil ? null : (lockTargets(p.pos, range + 2).find((t) => t.kind === c.kind && t.id === c.id) ?? null);
      // Free aim pointed well away from a chosen target (the cursor moved on to someone else) lets it go.
      const pointedAway = !!cur && freeAim !== null && Math.abs(angleDelta(freeAim, headingTo(p.pos, cur))) > FREE_AIM_KEEP_CONE;
      kept = cur;
      if (cur && !pointedAway && keepChosenLock(p.pos, cur, range, losKept)) {
        lock = cur;
        held = true;
        if (aimHeld) chosenUntil = now + CHOSEN_LOCK_IDLE;
      } else {
        if (cur?.down) lockHoldOffUntil = now + LOCK_REACQUIRE_DELAY;
        chosen = null;
        lock = null;
      }
    }
    // Lock-on while aiming: nearest standing target in the forward cone with a clear line (people first).
    if (held) {
      /* the chosen lock stands */
    } else if (aimHeld || now < holdUpUntil) {
      const targets = lockTargets(p.pos, range + 2);
      const keep = lock ? (targets.find((t) => t.kind === lock!.kind && t.id === lock!.id) ?? null) : null;
      kept = keep;
      // The man went down: a beat before the soft lock looks for the next one (FIRE held no longer carries on into him).
      if (keep?.down) lockHoldOffUntil = now + LOCK_REACQUIRE_DELAY;
      if (now < lockHoldOffUntil) lock = null;
      // Free aim picks only somebody close to where the cursor / right stick points; otherwise the facing cone.
      else if (freeAim !== null) lock = pickLockTarget(p.pos, freeAim, targets, range, losKept, keep, FREE_AIM_CONE, FREE_AIM_KEEP_CONE);
      else {
        lock = aimHeld || keep ? pickLockTarget(p.pos, p.heading, targets, range, losKept, keep) : null;
        // Nobody in the facing cone: the way the stick pushes (the man you are walking at, before the
        // damped turn has brought the body round to him).
        if (!lock && aimHeld) {
          const mh = moveHeading();
          if (mh !== null) lock = pickLockTarget(p.pos, mh, targets, range, los);
        }
      }
    } else lock = null;
    if (lock && (lock !== prevLock || los(p.pos, lock))) lockSeenAt = now;

    if (aimHeld) {
      p.setStance({ facing: lock ? headingTo(p.pos, lock) : freeAim, speedScale: fa ? 0.5 : 0.75 });
      stanceSet = true;
    } else if (fa && now < holdUpUntil && (lock || hipFacing !== null)) {
      // Fired from the hip: the gun is up for a moment and the body turns to the target it went at
      // (the round left towards the lock; the figure no longer fires out of its shoulder), full speed.
      // The man dropped by that round took the lock with him: the body still turns to where it went.
      p.setStance({ facing: lock ? headingTo(p.pos, lock) : hipFacing!, speedScale: 1 });
      stanceSet = true;
    } else if (action === 'swing' && swingFacing !== null) {
      p.setStance({ facing: swingFacing, speedScale: 0.6 });
      stanceSet = true;
    } else if (stanceSet) {
      p.setStance(null);
      stanceSet = false;
    }

    // A press is kept ATTACK_BUFFER s if it can't go off yet; a gun held down keeps firing as it comes ready.
    if (input.pressed('attack')) attackUntil = now + ATTACK_BUFFER;
    if (attackUntil >= now) {
      if (onAttack()) attackUntil = -1;
    } else if (fa && input.held('attack') && now - lastShotAt < fa.fireInterval * 3) onAttack(true);

    // Action progress.
    if (action === 'swing') {
      if (!swingLanded) lunge(p, w.melee.reach, w.melee.hitAt - actionT);
      actionT += dt;
      if (!swingLanded && actionT >= w.melee.hitAt) contact();
      if (actionT >= w.melee.swingTime) cancelAction();
    } else if (action === 'reload' && fa) {
      actionT += dt;
      if (actionT >= fa.reloadTime) {
        const n = ars().reload();
        cancelAction();
        persist();
        ctx.events.emit('weaponReloaded', { weapon: w.id, rounds: n, pos: { x: p.pos.x, z: p.pos.z } });
      }
    }
    if (fa && action === 'idle') {
      if (pendingReloadAt >= 0 && now >= pendingReloadAt) startReload();
      else if (!aimHeld && now - lastShotAt > TOP_UP_AFTER && now - lastAimAt > 1 && ars().canReload()) startReload();
    }

    if (online()) return; // brandishing and frightened passers-by are the offline police's business
    if (fa && raise > 0.6) brandish(now);
    else if (lock?.kind === 'ped' && now >= cowerAt) {
      // Squaring up to someone with a blade or a bat: they back off.
      cowerAt = now + 1.5;
      if (w.cls !== 'unarmed') ctx.services.tryGet('peds')?.panic({ x: lock.x, z: lock.z }, 1);
    }
  }

  /** A raised gun: coppers who can see it call it in; whoever it points at drops and covers up. */
  function brandish(now: number): void {
    const p = player()!;
    if (lock?.kind === 'ped' && now >= cowerAt) {
      cowerAt = now + 1;
      ctx.services.tryGet('peds')?.cower({ x: lock.x, z: lock.z }, 2.5, 3, 0.9);
    }
    if (now < brandishAt) return;
    brandishAt = now + BRANDISH_INTERVAL;
    const police = ctx.services.tryGet('police');
    if (!police) return;
    for (const o of police.observers()) {
      if (Math.hypot(o.x - p.pos.x, o.z - p.pos.z) > Math.min(BRANDISH_RANGE, o.range)) continue;
      if (!los({ x: o.x, z: o.z }, p.pos)) continue;
      crime('brandishing', 3, p.pos);
      return;
    }
  }

  // ---------------------------------------------------------------- pick-ups

  function placeNear(poi: PointOfInterest, along: number, side: number): V2 | null {
    const fx = Math.sin(poi.heading);
    const fz = Math.cos(poi.heading);
    const base = { x: poi.pos.x + fx * along - fz * side, z: poi.pos.z + fz * along + fx * side };
    for (let r = 0; r <= 6; r += 1) {
      for (let k = 0; k < (r === 0 ? 1 : 8); k++) {
        const a = (k / 8) * Math.PI * 2;
        const c = { x: base.x + Math.cos(a) * r, z: base.z + Math.sin(a) * r };
        if (ctx.physics.isShapeFree({ type: 'circle', r: 0.6 }, c, 0, Layer.STATIC)) return c;
      }
    }
    return null;
  }

  function spawnFound(): void {
    const layout = ctx.services.tryGet('layout');
    if (!layout) return;
    const done = new Set<string>();
    for (const s of FOUND_SPOTS) {
      if (done.has(`${s.weapon}:${s.poi}`)) continue;
      const poi = layout.pois.find((q) => q.id === s.poi);
      if (!poi) continue;
      const pos = placeNear(poi, s.along, s.side);
      if (!pos) continue;
      done.add(`${s.weapon}:${s.poi}`);
      pickups.push({ id: nextPickupId++, pos, kind: 'found', weapon: s.weapon, haul: null, until: 0, available: true });
    }
  }

  function dropHaul(h: Haul, pos: V2): void {
    const rounds = Object.values(h.reserve).reduce((a, b) => a + (b ?? 0), 0) + Object.values(h.loaded).reduce((a, b) => a + (b ?? 0), 0);
    if (h.weapons.length === 0 && rounds === 0) return;
    pickups.push({ id: nextPickupId++, pos: { x: pos.x, z: pos.z }, kind: 'dropped', weapon: h.weapons[0] ?? 'revolver', haul: h, until: ctx.state.time + DROPPED_LIFETIME, available: true });
    ctx.events.emit('weaponsDropped', { weapons: h.weapons, rounds, pos: { x: pos.x, z: pos.z } });
  }

  function updatePickups(): void {
    const now = ctx.state.time;
    const p = player();
    const active = isActive();
    for (let i = pickups.length - 1; i >= 0; i--) {
      const k = pickups[i]!;
      const d = p ? Math.hypot(p.pos.x - k.pos.x, p.pos.z - k.pos.z) : Infinity;
      if (k.kind === 'dropped' && now > k.until) {
        pickups.splice(i, 1);
        continue;
      }
      if (!k.available) {
        // A found prop comes back once its timer is up and nobody's watching it reappear.
        if (now > k.until && d > 40) k.available = true;
        continue;
      }
      if (!active || d > PICKUP_REACH) continue;
      if (k.kind === 'found') {
        if (arsenal.has(k.weapon)) continue;
        k.available = false;
        k.until = now + FOUND_RESPAWN;
        give(k.weapon, { select: true });
        ctx.events.emit('weaponPickedUp', { weapons: [k.weapon], rounds: 0, pos: { ...k.pos } });
        toast(`Picked up a ${WEAPONS[k.weapon].name.toLowerCase()}.`, 'good');
      } else if (k.haul) {
        const gotGuns = k.haul.weapons.filter((w) => !arsenal.has(w));
        const rounds = Object.values(k.haul.reserve).reduce((a, b) => a + (b ?? 0), 0);
        arsenal.takeHaul(k.haul);
        pickups.splice(i, 1);
        if (gotGuns[0]) arsenal.select(gotGuns[0]);
        changed();
        ctx.events.emit('weaponPickedUp', { weapons: k.haul.weapons, rounds, pos: { ...k.pos } });
        toast('Got your gear back off the pavement.', 'good');
      }
    }
  }

  // ---------------------------------------------------------------- the fence

  function placeFence(): void {
    const layout = ctx.services.tryGet('layout');
    if (!layout) return;
    // Gorringe's scrap yard in Cinder Yards; anywhere shady will do if the layout lacks it.
    const poi =
      layout.pois.find((q) => q.id === 'scrapyard-gorringe') ??
      layout.pois.find((q) => q.kind === 'hideout') ??
      layout.pois.find((q) => q.kind === 'lockup') ??
      layout.pois.find((q) => q.kind === 'pub');
    if (!poi) return;
    const pos = placeNear(poi, Math.min(poi.radius, 3), 0);
    if (!pos) return;
    fence = { pos, heading: poi.heading + Math.PI, name: FENCE_NAME };
  }

  function atFence(): boolean {
    const p = player();
    return !!fence && !!p && p.mode === 'foot' && Math.hypot(p.pos.x - fence.pos.x, p.pos.z - fence.pos.z) <= FENCE_REACH;
  }

  function buy(itemId: string): { ok: boolean; reason?: string } {
    if (online()) return { ok: false, reason: 'Not online. Sid only deals face to face.' };
    if (!atFence()) return { ok: false, reason: `You need to be at ${FENCE_NAME}'s.` };
    const item = fenceStock().find((s) => s.id === itemId);
    if (!item) return { ok: false, reason: 'He doesn\'t do that.' };
    const economy = ctx.services.tryGet('economy');
    if (item.kind === 'weapon' && arsenal.has(item.weapon)) return { ok: false, reason: 'You\'ve already got one.' };
    if (item.kind === 'ammo') {
      if (!arsenal.has(item.weapon)) return { ok: false, reason: 'No use without the shooter.' };
      if (arsenal.ammoRoom(item.weapon) <= 0) return { ok: false, reason: 'Pockets are full.' };
    }
    // Sid's price after your standing with him (contact ladder, WP-X1).
    const price = contactPrice(ctx, 'sid', item.price);
    if (economy ? !economy.spend(price, `fence:${item.id}`) : ctx.state.money < price) return { ok: false, reason: 'Not enough cash.' };
    if (!economy) {
      ctx.state.money -= price;
      ctx.events.emit('moneyChanged', { amount: ctx.state.money, delta: -price, reason: `fence:${item.id}` });
    }
    if (item.kind === 'weapon') give(item.weapon, { select: true });
    else {
      arsenal.addAmmo(item.weapon, WEAPONS[item.weapon].firearm!.ammo.box);
      persist();
    }
    ctx.events.emit('weaponBought', { item: item.id, price });
    return { ok: true };
  }

  // ---------------------------------------------------------------- service

  function info(): WeaponStateInfo {
    const ammo: Record<string, { loaded: number; reserve: number }> = {};
    for (const id of ars().list()) if (isFirearm(id)) ammo[id] = { loaded: ars().loaded(id), reserve: ars().reserve(id) };
    const fa = def().firearm;
    return {
      active: isActive(),
      current: ars().current,
      owned: ars().list(),
      loaded: ars().loaded(ars().current),
      reserve: ars().reserve(ars().current),
      ammo,
      raised: +raise.toFixed(2),
      action,
      reload: action === 'reload' && fa ? +(actionT / fa.reloadTime).toFixed(2) : 0,
      lock: lock ? { kind: lock.kind, id: lock.id, x: +lock.x.toFixed(2), z: +lock.z.toFixed(2), chosen: !!chosen } : null,
      fence: fence ? { x: +fence.pos.x.toFixed(2), z: +fence.pos.z.toFixed(2), name: fence.name, open: !!ctx.services.tryGet('backRoom')?.open } : null,
      pickups: pickups.map((k) => ({ id: k.id, weapon: k.weapon, kind: k.kind, x: +k.pos.x.toFixed(2), z: +k.pos.z.toFixed(2), available: k.available })),
      shots,
      hits,
    };
  }

  const service: WeaponService = {
    get active() {
      return isActive();
    },
    get current() {
      return ars().current;
    },
    get def() {
      return def();
    },
    get loaded() {
      return ars().loaded(ars().current);
    },
    get reserve() {
      return ars().reserve(ars().current);
    },
    get raised() {
      return raise;
    },
    get reloadProgress() {
      const fa = def().firearm;
      return action === 'reload' && fa ? Math.min(1, actionT / fa.reloadTime) : 0;
    },
    get lock() {
      return lock;
    },
    chooseLock,
    lockCandidates: (radius) => {
      const p = player();
      return p ? lockTargets(p.pos, radius) : [];
    },
    get anim() {
      return anim;
    },
    get sinceShot() {
      return ctx.state.time - lastShotAt;
    },
    owned: () => ars().list(),
    give,
    ammoRoom: (id) => ars().ammoRoom(id),
    addAmmo(id, n) {
      const added = ars().addAmmo(id, n);
      if (added > 0) persist();
      return added;
    },
    take(id) {
      if (online() || !arsenal.remove(id)) return false;
      changed();
      return true;
    },
    select(id) {
      if (online() || !arsenal.select(id)) return false;
      changed();
      return true;
    },
    pickups: () => pickups,
    get fence() {
      return fence;
    },
    buy,
    get shopOpen() {
      return !!ctx.services.tryGet('backRoom')?.open;
    },
    info,
  };

  function updateAnim(): void {
    const w = def();
    const now = ctx.state.time;
    anim.hold = w.hold;
    anim.raise = raise;
    if (action === 'swing') {
      anim.move = 'swing';
      anim.t = actionT / w.melee.swingTime;
    } else if (action === 'reload' && w.firearm) {
      anim.move = 'reload';
      anim.t = actionT / w.firearm.reloadTime;
    } else if (now - lastShotAt < RECOIL_TIME) {
      anim.move = 'fire';
      anim.t = (now - lastShotAt) / RECOIL_TIME;
    } else {
      anim.move = 'none';
      anim.t = 0;
    }
  }

  return {
    id: 'weapons',
    order: 205,
    init(c) {
      ctx = c;
      rng = c.rng.fork('weapons');
      c.services.provide('weapons', service);
      offs.push(
        c.events.on('playerBusted', () => {
          if (online()) return; // the server seizes the guns (they are items) and pushes the arsenal
          const h = arsenal.takeFirearms();
          if (h.weapons.length === 0 && Object.keys(h.reserve).length === 0) return;
          const rounds = Object.values(h.reserve).reduce((a, b) => a + (b ?? 0), 0) + Object.values(h.loaded).reduce((a, b) => a + (b ?? 0), 0);
          changed();
          c.events.emit('weaponsConfiscated', { weapons: h.weapons, rounds });
          if (h.weapons.length > 0) toast(`The Old Bill kept the ${h.weapons.map((w) => WEAPONS[w].short.toLowerCase()).join(' and the ')}.`, 'bad');
        }),
        c.events.on('playerWasted', (e) => {
          if (online()) return; // the server drops them in a world pile anyone can loot
          const h = arsenal.takeFirearms();
          changed();
          dropHaul(h, e.pos);
        }),
        c.events.on('newGameStarted', () => {
          arsenal.reset();
          pickups.splice(0, pickups.length, ...pickups.filter((k) => k.kind === 'found'));
          for (const k of pickups) k.available = true;
          changed();
        }),
        c.events.on('playerTeleported', () => {
          lock = null;
          chosen = null;
        }),
        c.events.on('netJoined', () => {
          aimSent = false;
          pendingEquip = null;
        }),
      );

      c.hooks.register('giveWeapon', (id, ammo) => {
        if (!isWeaponId(id)) throw new Error(`Unknown weapon '${id}'. Known: ${Object.keys(WEAPONS).join(', ')}`);
        if (online()) return false;
        give(id, { ammo: ammo ?? 0, select: true });
        return arsenal.has(id);
      });
      c.hooks.register('weaponState', () => info());
      c.hooks.register('aimWeapon', (on) => c.input.setVirtual({ aim: on }));
      c.hooks.register('fireWeapon', () => c.input.pulse('attack'));
      c.hooks.register('nextWeapon', () => c.input.pulse('weaponNext'));
      c.hooks.register('faceTowards', (x, z) => {
        const p = player();
        if (!p || p.mode !== 'foot') return;
        p.teleport({ x: p.pos.x, z: p.pos.z }, Math.atan2(x - p.pos.x, z - p.pos.z));
      });
      c.hooks.register('fenceBuy', (item) => buy(item));
      c.hooks.register('chooseLock', (kind, id) => (kind === null ? chooseLock(null) : chooseLock({ kind: kind as CombatTarget['kind'], id: id ?? 0 })));
      c.hooks.registerSnapshot('weapons', () => {
        const i = info();
        return { current: i.current, owned: i.owned, loaded: i.loaded, reserve: i.reserve, raised: i.raised, action: i.action, lock: i.lock, shots: i.shots, hits: i.hits };
      });
    },
    start() {
      const listen = (): void => {
        const net = ctx.services.tryGet('net');
        if (net) offs.push(net.onMessage('wpn', (d) => applyServer(d as WpnState)));
      };
      // The online client loads when the player goes online (net/onlineBoot.ts).
      const client = ctx.services.tryGet('onlineClient');
      if (client) client.whenLoaded(listen);
      else listen();
      const saved = ctx.services.tryGet('save')?.data.arsenal;
      if (saved) arsenal.load(saved);
      player()?.setBreakInScale(def().breakInScale);
      placeFence();
      spawnFound();
      // The door to Sid's back room (and the room itself) is the back-room system's (src/weapons/backRoom).
      if (fence) {
        const f = fence;
        const ui = ctx.services.tryGet('ui');
        // Named on the full map like the other places (and a tap there routes to it).
        const label = 'Sid’s back room — weapons';
        if (ui) offs.push(ui.addMarkerSource((out) => out.push({ x: f.pos.x, z: f.pos.z, kind: 'poi', glyph: 'F', pin: false, label })));
      }
    },
    fixedUpdate(dt) {
      const now = ctx.state.time;
      const isOnline = online();
      if (!isOnline) {
        updatePickups();
        // Health of people we've hit recovers after a quiet minute.
        if (ctx.state.frame % 60 === 0) for (const [k, h] of health) if (now - h.at > HP_RECOVER_AFTER) health.delete(k);
      }

      if (inControl() && ctx.input.pressed('weaponNext') && action !== 'swing') {
        ars().cycle();
        changed();
        if (isOnline) equipOnline(ars().current);
      }
      const active = isActive();
      if (!active) {
        if (isOnline) sendAim(false);
        attackUntil = -1;
        if (wasActive || stanceSet) {
          lock = null;
          chosen = null;
          if (action !== 'idle') cancelAction();
          if (stanceSet) player()?.setStance(null);
          stanceSet = false;
        }
        raise = approach(raise, 0, dt * 6);
        if (wasActive) ctx.services.tryGet('cameraRig')?.setAim(null);
        wasActive = false;
        updateAnim();
        return;
      }
      wasActive = true;
      updateHand(dt);
      updateAnim();
      // The camera leans towards what the gun is pointed at (a lock, while the gun is up).
      ctx.services.tryGet('cameraRig')?.setAim(lock && (raise > 0.3 || ctx.input.held('aim')) ? lock : null);
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
    },
  };
}
